/* eslint-disable react-hooks/exhaustive-deps */
import { useAlert, useCompany } from '@orientaction/hooks';
import { BookTemplate, servicesService } from '@orientaction/services';
import { getFromLS } from '@orientaction/utils';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { getUserById } from '../../../services/user.service';
import { apiServiceNative } from '../../../utils/fetchNative';
import { getUserPrestations } from '../BeneficiaryProfile/utils';
import { fetchAssignableStaffUsers } from '../shared/assignableStaff';
import {
  A_LA_CARTE_SERVICE_ID,
  BILAN_COMPETENCES_SERVICE_ID,
  CONSULTANT_ROLE_ID,
  OUTPLACEMENT_SERVICE_ID,
} from '../shared/constants';
import { buildServiceResourcesEntry } from '../shared/serviceResourcesMap';
import {
  filterMinorCategoriesForStaff,
  getStaffRoleLabel,
  normalizeServiceList,
  readRole,
  readUpperHierarchy,
  resourceMatchesService,
  sortAssignableStaff,
  toId,
  toResourceFromTemplate,
  unwrapCollection,
  unwrapRelation,
} from '../shared/utils';
import { ADD_PRESTATION_ALREADY_EXISTS, ADD_PRESTATION_SUCCESS } from './constants';
import {
  AddPrestationStep,
  ConsultantOption,
  ResourceItem,
  ResourceKind,
  ServiceOption,
} from './types';

const toSelectedBooksPayload = (selected: ResourceItem[]) =>
  selected.map(item => ({
    id: item.id,
    query: item.query,
    isChecked: true,
  }));

const toSelectedTestsPayload = (selected: ResourceItem[]) =>
  selected.map(item => ({
    id: item.id,
    isChecked: true,
  }));

const toIdObjects = (selected: ResourceItem[]) => selected.map(item => ({ id: item.id }));

export const useAddPrestation = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const alert = useAlert();

  const token = getFromLS('userToken') || '';
  const userConnected = JSON.parse(getFromLS('userInfos') || '{}');
  const connectedUserId = userConnected?.id;
  const isConsultant = userConnected?.company_user_role?.id === CONSULTANT_ROLE_ID;
  const { companies } = useCompany();

  const [user, setUser] = useState<any>(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [consultants, setConsultants] = useState<ConsultantOption[]>([]);
  const [consultantId, setConsultantId] = useState<number | ''>(
    isConsultant && connectedUserId ? Number(connectedUserId) : ''
  );
  const [availableServices, setAvailableServices] = useState<ServiceOption[]>([]);

  const [step, setStep] = useState<AddPrestationStep>(1);
  const [serviceId, setServiceId] = useState<number | ''>('');

  const [majorCatalog, setMajorCatalog] = useState<ResourceItem[]>([]);
  const [minorCatalog, setMinorCatalog] = useState<ResourceItem[]>([]);
  const [bookCatalog, setBookCatalog] = useState<ResourceItem[]>([]);
  const [gameCatalog, setGameCatalog] = useState<ResourceItem[]>([]);
  const [ebookCatalog, setEbookCatalog] = useState<ResourceItem[]>([]);

  const [majorTests, setMajorTests] = useState<ResourceItem[]>([]);
  const [minorTests, setMinorTests] = useState<ResourceItem[]>([]);
  const [books, setBooks] = useState<ResourceItem[]>([]);
  const [games, setGames] = useState<ResourceItem[]>([]);
  const [ebooks, setEbooks] = useState<ResourceItem[]>([]);

  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const beneficiaryFullName = useMemo(() => {
    if (!user) {
      return '';
    }
    return `${user.firstname || ''} ${user.lastname || ''}`.trim();
  }, [user]);

  const alreadyAttachedServiceIds = useMemo(
    () => getUserPrestations(user).map(service => Number(service.id)),
    [user]
  );

  const selectedConsultant = useMemo(
    () => consultants.find(item => Number(item.id) === Number(consultantId)) || null,
    [consultants, consultantId]
  );

  const companyAlias = useMemo(() => {
    const companyId = Number(
      user?.company?.id || user?.company?.data?.id || userConnected?.company?.id
    );
    return (companies || []).find((item: any) => Number(item.id) === companyId)?.attributes
      ?.companyUserRoleAlias;
  }, [companies, user, userConnected?.company?.id]);

  const goToPrestationsTab = () => {
    history.push(`/admin/users-resultats/beneficiaire/${id}?tab=prestations`);
  };

  useEffect(() => {
    let cancelled = false;

    const loadBeneficiary = async () => {
      setLoadingUser(true);
      try {
        const response = await getUserById({ token, userId: id });
        const next = response?.data || response;
        if (cancelled) {
          return;
        }
        setUser(next);
        if (!isConsultant) {
          const hierarchy = readUpperHierarchy(next?.upper_hierarchy);
          if (hierarchy?.id) {
            setConsultantId(Number(hierarchy.id));
          }
        }
      } catch (error) {
        if (!cancelled) {
          alert.openSimpleAlert();
        }
      } finally {
        if (!cancelled) {
          setLoadingUser(false);
        }
      }
    };

    loadBeneficiary();
    return () => {
      cancelled = true;
    };
  }, [id, token]);

  useEffect(() => {
    if (!user) {
      return undefined;
    }

    let cancelled = false;
    const companyId =
      Number(user?.company?.id || user?.company?.data?.id || userConnected?.company?.id) || 1;

    const mapStaffOption = (item: any, categoryTestIds?: number[]): ConsultantOption => {
      const role = readRole(item.company_user_role) || undefined;
      return {
        id: Number(item.id),
        firstname: item.firstname || '',
        lastname: item.lastname || '',
        company_user_role: role,
        roleLabel: getStaffRoleLabel(role, companyAlias),
        allowedServices: normalizeServiceList(item.allowed_services),
        categoryTestIds:
          categoryTestIds ||
          (item.category_tests || []).map((category: any) => Number(category.id)),
      };
    };

    const loadConsultants = async () => {
      try {
        const users = await fetchAssignableStaffUsers({
          companyId,
          consultantOnly: isConsultant,
        });
        if (cancelled) {
          return;
        }
        let list: ConsultantOption[] = users.map(item => mapStaffOption(item));

        if (
          isConsultant &&
          connectedUserId &&
          !list.some(item => Number(item.id) === Number(connectedUserId))
        ) {
          list = [mapStaffOption(userConnected), ...list];
        }

        const currentHierarchy = readUpperHierarchy(user?.upper_hierarchy);
        if (
          currentHierarchy?.id &&
          !list.some(item => Number(item.id) === Number(currentHierarchy.id))
        ) {
          list = [mapStaffOption(currentHierarchy, []), ...list];
        }
        setConsultants(sortAssignableStaff(list));
      } catch (error) {
        if (!cancelled) {
          alert.openSimpleAlert();
        }
      }
    };

    loadConsultants();
    return () => {
      cancelled = true;
    };
  }, [user, connectedUserId, isConsultant, companyAlias]);

  useEffect(() => {
    if (!consultantId) {
      setAvailableServices([]);
      return undefined;
    }

    let cancelled = false;

    const loadServicesForConsultant = async () => {
      const consultant = consultants.find(item => Number(item.id) === Number(consultantId));
      let fromAllowed = consultant?.allowedServices || [];
      if (!fromAllowed.length) {
        try {
          const response = await servicesService.getUserServices(token, consultantId);
          fromAllowed = normalizeServiceList(response?.data?.data || response?.data);
        } catch {
          fromAllowed = [];
        }
      }
      if (cancelled) {
        return;
      }
      if (!fromAllowed.some(item => Number(item.id) === A_LA_CARTE_SERVICE_ID)) {
        fromAllowed = [...fromAllowed, { id: A_LA_CARTE_SERVICE_ID, name: 'A la carte' }];
      }
      setAvailableServices(fromAllowed);
    };

    loadServicesForConsultant();
    return () => {
      cancelled = true;
    };
  }, [consultantId, consultants, token]);

  useEffect(() => {
    setServiceId('');
    setMajorTests([]);
    setMinorTests([]);
    setBooks([]);
    setGames([]);
    setEbooks([]);
    setMajorCatalog([]);
    setMinorCatalog([]);
    setBookCatalog([]);
    setGameCatalog([]);
    setEbookCatalog([]);
  }, [consultantId]);

  useEffect(() => {
    setMajorTests([]);
    setMinorTests([]);
    setBooks([]);
    setGames([]);
    setEbooks([]);
    setMajorCatalog([]);
    setMinorCatalog([]);
    setBookCatalog([]);
    setGameCatalog([]);
    setEbookCatalog([]);
  }, [serviceId]);

  useEffect(() => {
    if (step !== 2 || !serviceId || !selectedConsultant) {
      return undefined;
    }

    let cancelled = false;

    const loadResourceCatalogs = async () => {
      setLoadingCatalogs(true);
      try {
        const populate = {
          major_tests_templates: { fields: ['id', 'name', 'query'], sort: ['id:asc'] },
          book_templates: {
            fields: ['id', 'name', 'query', 'route', 'HourRead'],
            sort: ['id:asc'],
          },
        };
        const skipMinors = Number(serviceId) === OUTPLACEMENT_SERVICE_ID;
        let categoriesPromise: Promise<any> = Promise.resolve({ data: [] });
        if (!skipMinors) {
          const stored = sessionStorage.getItem('categories-test');
          if (stored) {
            try {
              categoriesPromise = Promise.resolve(JSON.parse(stored));
            } catch {
              categoriesPromise = apiServiceNative.get('/api/category-tests?populate=tests');
            }
          } else {
            categoriesPromise = apiServiceNative.get('/api/category-tests?populate=tests');
          }
        }
        const [serviceRes, gamesRes, ebooksRes, categoriesRes] = await Promise.all([
          servicesService.getServiceById(token, serviceId, ['id', 'name'], {}, populate),
          apiServiceNative.get('/api/game-list'),
          apiServiceNative.get('/api/ebook-list'),
          categoriesPromise,
        ]);

        if (cancelled) {
          return;
        }

        const servicePayload = serviceRes?.data?.data || serviceRes?.data || serviceRes;
        const serviceAttrs = servicePayload?.attributes || servicePayload;

        setMajorCatalog(
          unwrapRelation(serviceAttrs?.major_tests_templates).map((item: any) =>
            toResourceFromTemplate(item)
          )
        );

        let bookRaw = unwrapRelation(serviceAttrs?.book_templates);
        if (!bookRaw.length && Number(serviceId) === BILAN_COMPETENCES_SERVICE_ID) {
          const booksRes = await BookTemplate.getUserBooks(token, { userId: 0 });
          bookRaw = booksRes?.data?.books || [];
        }
        if (cancelled) {
          return;
        }
        setBookCatalog(bookRaw.map((item: any) => toResourceFromTemplate(item)));

        const gameList = unwrapCollection(gamesRes?.data || gamesRes);
        setGameCatalog(
          gameList
            .filter((game: any) => game.status !== 'draft')
            .filter((game: any) => resourceMatchesService(game, Number(serviceId)))
            .map((game: any) => ({
              id: Number(game.id),
              label: game.title || game.name || `Serious game ${game.id}`,
            }))
        );

        const ebookList = unwrapCollection(ebooksRes?.data || ebooksRes);
        setEbookCatalog(
          ebookList
            .filter((ebook: any) => ebook.status !== 'draft')
            .filter((ebook: any) => resourceMatchesService(ebook, Number(serviceId)))
            .map((ebook: any) => ({
              id: Number(ebook.id),
              label: ebook.title || ebook.name || `Ebook ${ebook.id}`,
            }))
        );

        const categories = filterMinorCategoriesForStaff(
          unwrapCollection(categoriesRes?.data || categoriesRes),
          {
            company_user_role: selectedConsultant.company_user_role,
            categoryIds: selectedConsultant.categoryTestIds,
          }
        ).sort(
          (a: any, b: any) =>
            Number(a.attributes?.priority || a.priority || 0) -
            Number(b.attributes?.priority || b.priority || 0)
        );

        const minors: ResourceItem[] = [];
        categories.forEach((category: any) => {
          const group = category.attributes?.name || category.name || '';
          const groupSubtitle = category.attributes?.subtitle || category.subtitle || '';
          const groupId = toId(category);
          const tests = unwrapRelation(category.attributes?.tests || category.tests)
            .filter((test: any) => {
              const attrs = test.attributes || test;
              return !attrs.isArchived && !attrs.isDesabled;
            })
            .sort(
              (a: any, b: any) =>
                Number(a.attributes?.priority || a.priority || 0) -
                Number(b.attributes?.priority || b.priority || 0)
            );
          tests.forEach((test: any) => {
            minors.push(toResourceFromTemplate(test, group, groupSubtitle, groupId));
          });
        });
        setMinorCatalog(minors);
      } catch (error) {
        if (!cancelled) {
          alert.openSimpleAlert();
        }
      } finally {
        if (!cancelled) {
          setLoadingCatalogs(false);
        }
      }
    };

    loadResourceCatalogs();
    return () => {
      cancelled = true;
    };
  }, [step, serviceId, selectedConsultant, token]);

  const toggleResource = (kind: ResourceKind, item: ResourceItem, checked: boolean) => {
    const setters: Record<ResourceKind, Dispatch<SetStateAction<ResourceItem[]>>> = {
      majorTests: setMajorTests,
      minorTests: setMinorTests,
      books: setBooks,
      games: setGames,
      ebooks: setEbooks,
    };
    const current: Record<ResourceKind, ResourceItem[]> = {
      majorTests,
      minorTests,
      books,
      games,
      ebooks,
    };
    const list = current[kind];
    if (checked) {
      if (list.some(entry => entry.id === item.id)) {
        return;
      }
      setters[kind]([...list, item]);
      return;
    }
    setters[kind](list.filter(entry => entry.id !== item.id));
  };

  const removeResource = (kind: ResourceKind, resourceId: number) => {
    const setters: Record<ResourceKind, Dispatch<SetStateAction<ResourceItem[]>>> = {
      majorTests: setMajorTests,
      minorTests: setMinorTests,
      books: setBooks,
      games: setGames,
      ebooks: setEbooks,
    };
    const current: Record<ResourceKind, ResourceItem[]> = {
      majorTests,
      minorTests,
      books,
      games,
      ebooks,
    };
    setters[kind](current[kind].filter(entry => entry.id !== resourceId));
  };

  const canGoNext = Boolean(consultantId) && Boolean(serviceId);

  const goNext = () => {
    if (step === 1 && canGoNext) {
      setStep(2);
    }
  };

  const goPrevious = () => {
    if (step === 1) {
      goToPrestationsTab();
      return;
    }
    setStep(1);
  };

  const handleSubmit = async () => {
    if (!user || !serviceId || !consultantId) {
      return;
    }
    if (alreadyAttachedServiceIds.some(existing => Number(existing) === Number(serviceId))) {
      alert.setOpenAlert(true, ADD_PRESTATION_ALREADY_EXISTS, 'error');
      return;
    }

    setSubmitting(true);
    try {
      const selectedService = availableServices.find(item => Number(item.id) === Number(serviceId));
      if (!selectedService) {
        alert.openSimpleAlert();
        return;
      }

      const serviceKey = String(selectedService.id);
      const selectedConsultantId = Number(consultantId);
      const selectedResources = buildServiceResourcesEntry({
        upperHierarchy: selectedConsultantId,
        minorTests,
        books,
        majorTests,
        games,
        ebooks,
      });
      const payload = {
        services: [{ id: Number(selectedService.id), name: selectedService.name }],
        upper_hierarchy: { id: selectedConsultantId },
        service_resources_map: {
          [serviceKey]: {
            upper_hierarchy: selectedResources.upper_hierarchy || selectedConsultantId,
            tests_to_do: selectedResources.tests_to_do || [],
            book_template_ids: selectedResources.book_template_ids || [],
            major_test_ids: selectedResources.major_test_ids || [],
            authorized_games: selectedResources.authorized_games || [],
            authorized_ebooks: selectedResources.authorized_ebooks || [],
          },
        },
        books: toSelectedBooksPayload(books),
        tests: toSelectedTestsPayload(majorTests),
        tests_to_do: toIdObjects(minorTests),
        authorized_games: toIdObjects(games),
        authorized_ebooks: toIdObjects(ebooks),
      };

      await apiServiceNative.put(`/api/company-user-admin-update-services/${user.id}`, payload);

      alert.setOpenAlert(true, ADD_PRESTATION_SUCCESS, 'success');
      goToPrestationsTab();
    } catch (error) {
      alert.openSimpleAlert();
    } finally {
      setSubmitting(false);
    }
  };

  return {
    id,
    step,
    loadingUser,
    submitting,
    loadingCatalogs,
    beneficiaryFullName,
    consultants,
    consultantId,
    setConsultantId,
    lockConsultant: isConsultant,
    availableServices,
    alreadyAttachedServiceIds,
    serviceId,
    setServiceId,
    majorCatalog,
    minorCatalog,
    bookCatalog,
    gameCatalog,
    ebookCatalog,
    majorTests,
    minorTests,
    books,
    games,
    ebooks,
    canGoNext,
    goNext,
    goPrevious,
    goToPrestationsTab,
    toggleResource,
    removeResource,
    handleSubmit,
  };
};
